import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import classNames from 'classnames';
import { fabric } from 'fabric';
import 'src/fabric-overrides';
import useUserPicture from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useUserPicture';
import { renderPdfPageAnnotationsOnCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/utils/renderPdfPageAnnotationsOnCanvas';
import { useAssetViewerState } from '@hooks/useAssetViewerState';

import './PdfPageDisplayCanvas.less';

type PdfPageDisplayCanvasProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  zoomFactor: number;
  turnoverDegree: number;
  visible: boolean;
  interactive: boolean;
};

function PdfPageDisplayCanvas({
  pageNumber,
  pageWidth,
  pageHeight,
  zoomFactor,
  turnoverDegree,
  visible,
  interactive
}: PdfPageDisplayCanvasProps) {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const {
    commentsList,
    isHideComments,
    selectComment,
    setActivePageNumber,
    setCanvasMode,
    setDocumentHighlight
  } = useAssetViewerState();
  const { getUserPicture } = useUserPicture();

  const onSelectComment = useCallback(
    (commentId: string) => {
      selectComment(commentId);
      setActivePageNumber(pageNumber);
    },
    [pageNumber, setActivePageNumber, selectComment]
  );

  useLayoutEffect(() => {
    if (!visible || !canvasElRef.current || !pageWidth || !pageHeight) {
      return undefined;
    }

    const canvas = new fabric.Canvas(canvasElRef.current, {
      selectionColor: 'transparent',
      selectionLineWidth: 0,
      centeredScaling: true,
      targetFindTolerance: 5,
      preserveObjectStacking: true,
      perPixelTargetFind: true,
      enableRetinaScaling: false,
      imageSmoothingEnabled: false,
      width: pageWidth,
      height: pageHeight,
      selection: false,
      allowTouchScrolling: false
    });

    fabricCanvasRef.current = canvas;

    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, [visible, pageWidth, pageHeight]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !canvas || !pageWidth || !pageHeight) return;

    canvas.setWidth(pageWidth);
    canvas.setHeight(pageHeight);

    renderPdfPageAnnotationsOnCanvas({
      canvas,
      pageNumber,
      commentsList,
      zoomFactor,
      turnoverDegree,
      isHideComments,
      getUserPicture,
      onSelectComment,
      viewerActions: {
        selectComment,
        setCanvasMode,
        setDocumentHighlight
      }
    });
  }, [
    visible,
    pageNumber,
    pageWidth,
    pageHeight,
    commentsList,
    zoomFactor,
    turnoverDegree,
    isHideComments,
    getUserPicture,
    onSelectComment,
    selectComment,
    setCanvasMode,
    setDocumentHighlight
  ]);

  if (!visible || !pageWidth || !pageHeight) return null;

  return (
    <div
      className={classNames('media_viewer_pdf-page-display-canvas', {
        'b-non-interactive': !interactive
      })}
    >
      <canvas ref={canvasElRef} />
    </div>
  );
}

export default memo(PdfPageDisplayCanvas);
